import React from "react";
import { useI18n } from "../lib/i18n";

/**
 * Terms & Conditions + Privacy Policy.
 *
 * Single combined page (per user request) — the legal text was provided
 * verbatim by the firm's lawyers. The page is rendered as a long-form
 * read with a sticky in-page table of contents on desktop.
 *
 * NOTE: The legal Arabic text is treated as the canonical version. The
 * English summary is a short one-paragraph notice that points users to
 * the Arabic text for the binding terms (per Kuwaiti law and the firm's
 * primary jurisdiction).
 */

const SECTIONS = [
  { id: "definitions", label: "أولاً: التعريفات" },
  { id: "acceptance", label: "ثانياً: قبول الشروط" },
  { id: "nature", label: "ثالثاً: طبيعة الموقع" },
  { id: "account", label: "رابعاً: إنشاء الحساب" },
  { id: "ai-services", label: "خامساً: خدمات الذكاء الاصطناعي" },
  { id: "case-assessment", label: "سادساً: تقييم القضايا" },
  { id: "documents", label: "سابعاً: مراجعة المستندات" },
  { id: "user-data", label: "ثامناً: مسؤولية البيانات" },
  { id: "privacy-collection", label: "تاسعاً: الخصوصية وجمع البيانات" },
  { id: "data-sharing", label: "عاشراً: مشاركة البيانات" },
  { id: "sensitive", label: "الحادي عشر: البيانات الحساسة" },
  { id: "confidentiality", label: "الثاني عشر: سرية المعلومات" },
  { id: "no-attorney", label: "الثالث عشر: عدم إنشاء علاقة محاماة تلقائية" },
  { id: "no-guarantee", label: "الرابع عشر: عدم ضمان النتائج" },
  { id: "user-obligations", label: "الخامس عشر: التزامات المستخدم" },
  { id: "content", label: "السادس عشر: المحتوى القانوني المنشور" },
  { id: "contracts", label: "السابع عشر: العقود والنماذج" },
  { id: "fees", label: "الثامن عشر: الرسوم والمدفوعات" },
  { id: "refusal", label: "التاسع عشر: رفض أو تعليق الخدمة" },
  { id: "liability", label: "العشرون: حدود المسؤولية" },
  { id: "security", label: "الحادي والعشرون: أمن المعلومات" },
  { id: "cookies", label: "الثاني والعشرون: ملفات تعريف الارتباط" },
  { id: "retention", label: "الثالث والعشرون: حفظ البيانات وحذفها" },
  { id: "ip", label: "الرابع والعشرون: حقوق الملكية الفكرية" },
  { id: "comms", label: "الخامس والعشرون: الاتصالات الإلكترونية" },
  { id: "jurisdiction", label: "السادس والعشرون: القانون والاختصاص" },
  { id: "acknowledgement", label: "السابع والعشرون: الإقرار النهائي" },
];

const Para = ({ children }) => (
  <p className="text-slate-700 leading-[1.95] mb-4">{children}</p>
);
const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="font-display text-2xl lg:text-3xl text-slate-900 mt-14 mb-5 scroll-mt-24"
  >
    {children}
  </h2>
);
const Ul = ({ children }) => (
  <ul className="list-disc ps-6 mb-5 space-y-2 text-slate-700 leading-[1.9]">
    {children}
  </ul>
);

export default function Terms() {
  const { lang } = useI18n();

  // Show English notice + link to Arabic when in EN mode
  if (lang === "en") {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20" dir="ltr" data-testid="terms-page-en">
        <p className="eyebrow text-slate-500 mb-3">Legal</p>
        <h1 className="font-display text-4xl lg:text-5xl text-slate-900 mb-8">
          Terms &amp; Conditions and Privacy Policy
        </h1>
        <p className="text-slate-700 leading-relaxed mb-5">
          The binding Terms &amp; Conditions and Privacy Policy governing your
          use of ALDAKHEELLG.COM, your account, uploaded files, AI-assisted
          services (Ask&nbsp;Sami, Case Assessment, Document Review, AI
          Contracts), and any related services are published in Arabic — the
          firm's primary working language and the canonical text under
          Kuwaiti law.
        </p>
        <p className="text-slate-700 leading-relaxed mb-8">
          By using the site, registering an account, uploading documents,
          or using any AI service, you confirm that you have read and
          accepted those terms. The full Arabic version is available below.
        </p>
        <a
          href="?lang=ar"
          onClick={(e) => {
            e.preventDefault();
            try {
              localStorage.setItem("lang", "ar");
            } catch (_) { /* noop */ }
            window.location.reload();
          }}
          className="inline-flex items-center px-5 py-3 bg-slate-900 text-white text-sm rounded-sm hover:bg-slate-800"
          data-testid="terms-switch-to-ar"
        >
          عرض النص الكامل بالعربية
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" dir="rtl" data-testid="terms-page">
      <header className="mb-12 lg:mb-16">
        <p className="eyebrow text-slate-500 mb-3">قانوني</p>
        <h1 className="font-display text-4xl lg:text-5xl text-slate-900 leading-tight">
          الشروط والأحكام وسياسة الخصوصية
        </h1>
        <p className="mt-5 max-w-3xl text-slate-600 leading-relaxed">
          تسري هذه الشروط والأحكام وسياسة الخصوصية على استخدام موقع
          ALDAKHEELLG.COM وجميع الخدمات المرتبطة به، بما في ذلك خدمات
          الاستشارات القانونية، تقييم القضايا، مراجعة المستندات، العقود،
          خدمات الذكاء الاصطناعي، إنشاء الحساب، رفع الملفات، التواصل مع
          المكتب، وأي خدمات حالية أو مستقبلية تُقدَّم من خلال الموقع.
        </p>
        <p className="mt-4 max-w-3xl text-slate-600 leading-relaxed">
          باستخدامك للموقع أو إنشاء حساب أو رفع مستند أو إدخال بيانات أو
          استخدام خدمات الذكاء الاصطناعي، فإنك تقر بأنك قرأت هذه الشروط
          وفهمتها ووافقت عليها بالكامل.
        </p>
      </header>

      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        {/* TOC */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <div className="lg:sticky lg:top-24 border border-slate-200 bg-slate-50 p-5 rounded">
            <p className="eyebrow text-slate-500 mb-3">المحتويات</p>
            <ul className="space-y-2 text-sm">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-slate-700 hover:text-slate-900 hover:underline underline-offset-4 leading-snug block"
                  >
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Body */}
        <article className="lg:col-span-9 order-1 lg:order-2 max-w-3xl">
          <H2 id="definitions">أولاً: التعريفات</H2>
          <Ul>
            <li><b>الموقع:</b> موقع ALDAKHEELLG.COM وجميع الصفحات والأنظمة والخدمات الإلكترونية التابعة له.</li>
            <li><b>المكتب:</b> Aldakheel Legal Group أو الجهة القانونية المشغلة للموقع.</li>
            <li><b>المستخدم / طالب الخدمة:</b> كل شخص طبيعي أو اعتباري يستخدم الموقع أو ينشئ حساباً أو يطلب خدمة أو يرفع بيانات أو مستندات.</li>
            <li><b>الخدمات:</b> تشمل، دون حصر، الاستشارات القانونية، تقييم القضايا، مراجعة المستندات، تحليل العقود، إعداد النماذج، التواصل مع المكتب، حجز الاستشارات، وخدمات الذكاء الاصطناعي.</li>
            <li><b>خدمات الذكاء الاصطناعي:</b> أي خدمة آلية أو شبه آلية تقدم إجابات أو تحليلات أو ملخصات أو اقتراحات أو مسودات أو مراجعات أولية، مثل خدمة تقييم القضية أو مراجعة المستندات أو إنشاء العقود أو خدمة Ask Sami أو أي خدمة مشابهة.</li>
            <li><b>البيانات:</b> أي معلومات أو مستندات أو صور أو ملفات أو وقائع أو مراسلات أو بيانات شخصية أو قانونية أو تجارية يقوم المستخدم بإدخالها أو رفعها أو إرسالها عبر الموقع.</li>
          </Ul>

          <H2 id="acceptance">ثانياً: قبول الشروط</H2>
          <Para>يعد استخدام الموقع أو أي من خدماته قبولاً صريحاً من المستخدم بهذه الشروط والأحكام وسياسة الخصوصية.</Para>
          <Para>ويقر المستخدم بأن استخدامه للموقع يتم بإرادته الحرة وعلى مسؤوليته الشخصية، وأنه يملك الأهلية القانونية لاستخدام الموقع وطلب الخدمات.</Para>
          <Para>ويحق للموقع تعديل هذه الشروط في أي وقت، وتصبح التعديلات نافذة من تاريخ نشرها على الموقع، ويعد استمرار استخدام الموقع بعد النشر قبولاً للتعديلات.</Para>

          <H2 id="nature">ثالثاً: طبيعة موقع ALDAKHEELLG.COM</H2>
          <Para>الموقع منصة رقمية قانونية تابعة لمكتب محاماة كويتي، وتقدم خدمات قانونية ورقمية تشمل عرض مجالات الممارسة القانونية، التواصل مع المكتب، تقييم القضايا، مراجعة المستندات، وخدمات مدعومة بالذكاء الاصطناعي.</Para>
          <Para>ومن هذه الخدمات تقييم القضية، حيث يصف المستخدم حالته ليقدم النظام ملخصاً للنزاع والنقاط القانونية والمستندات الناقصة والخطوات التالية، وكذلك خدمة مراجعة المستندات التي تتيح رفع عقد أو إنذار أو حكم أو لصق النص لتحليله واستخراج المخاطر والبنود الخطرة واقتراح الملاحظات.</Para>

          <H2 id="account">رابعاً: إنشاء الحساب</H2>
          <Para>عند إنشاء حساب في الموقع، يلتزم المستخدم بما يلي:</Para>
          <Ul>
            <li>تقديم بيانات صحيحة ودقيقة ومحدثة.</li>
            <li>عدم استخدام اسم أو هوية أو بريد إلكتروني أو رقم هاتف لا يخصه.</li>
            <li>المحافظة على سرية بيانات الدخول وكلمة المرور.</li>
            <li>تحمل كامل المسؤولية عن أي استخدام يتم من خلال حسابه.</li>
            <li>إخطار إدارة الموقع فوراً عند الاشتباه في استخدام غير مصرح به للحساب.</li>
            <li>عدم مشاركة الحساب مع أي شخص آخر.</li>
            <li>تحديث بياناته عند حدوث أي تغيير.</li>
          </Ul>
          <Para>ولا يتحمل الموقع أو المكتب أي مسؤولية عن أي ضرر ينتج عن إهمال المستخدم في حماية حسابه أو مشاركة بيانات الدخول مع الغير.</Para>
          <Para>ويحق للموقع تعليق أو إلغاء الحساب في حال إساءة الاستخدام أو تقديم بيانات غير صحيحة أو مخالفة هذه الشروط أو استخدام الموقع بطريقة غير مشروعة.</Para>

          <H2 id="ai-services">خامساً: استخدام خدمات الذكاء الاصطناعي</H2>
          <Para>يقر المستخدم بأن خدمات الذكاء الاصطناعي في الموقع هي خدمات مساعدة وأولية، وتهدف إلى تنظيم المعلومات، تلخيصها، تحليلها مبدئياً، اقتراح خطوات عامة، أو إعداد مسودات أولية.</Para>
          <Para>ولا تُعدّ مخرجات الذكاء الاصطناعي رأياً قانونياً نهائياً، ولا تغني عن مراجعة محامٍ مختص، ولا تنشئ بذاتها علاقة محاماة أو وكالة قانونية بين المستخدم والمكتب.</Para>
          <Para>ويقر المستخدم بما يلي:</Para>
          <Ul>
            <li>أن مخرجات الذكاء الاصطناعي قد تكون غير دقيقة أو غير مكتملة أو غير مناسبة لحالته.</li>
            <li>أن الذكاء الاصطناعي يعتمد على البيانات التي يدخلها المستخدم.</li>
            <li>أن أي خطأ أو نقص في البيانات قد يؤدي إلى نتيجة غير صحيحة أو غير مكتملة.</li>
            <li>أن الاعتماد على مخرجات الذكاء الاصطناعي دون مراجعة قانونية متخصصة يكون على مسؤولية المستخدم وحده.</li>
            <li>أن الخدمات الآلية لا تمثل ضماناً لقبول القضية أو نجاحها أو تحقيق نتيجة قانونية معينة.</li>
            <li>أن الرأي القانوني النهائي لا يصدر إلا بعد مراجعة الوقائع والمستندات من محامٍ مختص وقبول الخدمة صراحة من المكتب.</li>
          </Ul>

          <H2 id="case-assessment">سادساً: تقييم القضايا</H2>
          <Para>في حال استخدام خدمة تقييم القضية أو أي خدمة مشابهة، يقر المستخدم بأن المعلومات التي يقدمها يتم تحليلها مبدئياً لتقديم تصور عام عن النزاع أو المسألة القانونية.</Para>
          <Para>ولا يعد التقييم الصادر من النظام حكماً قانونياً نهائياً، ولا يعتبر قبولاً للقضية، ولا يترتب عليه التزام من المكتب بتمثيل المستخدم أو اتخاذ أي إجراء قانوني باسمه.</Para>
          <Para>ويقر المستخدم بأن أي توصية أو خطوة مقترحة تحتاج إلى مراجعة قانونية مستقلة قبل اتخاذ أي إجراء.</Para>

          <H2 id="documents">سابعاً: مراجعة المستندات ورفع الملفات</H2>
          <Para>عند رفع عقد أو حكم أو إنذار أو مذكرة أو أي مستند آخر، يقر المستخدم بما يلي:</Para>
          <Ul>
            <li>أنه يملك الحق القانوني في رفع المستند ومشاركته.</li>
            <li>أن المستند لا يحتوي على بيانات تخص الغير إلا إذا كان يملك الصفة أو الإذن القانوني لمشاركتها.</li>
            <li>أن رفع المستند يتم على مسؤوليته الشخصية.</li>
            <li>أن التحليل الصادر من الموقع أو الذكاء الاصطناعي هو تحليل أولي ولا يغني عن المراجعة القانونية.</li>
            <li>أن الموقع لا يضمن اكتشاف جميع المخاطر أو البنود أو الأخطاء أو الثغرات في المستند.</li>
            <li>أن جودة التحليل تعتمد على وضوح المستند واكتمال البيانات وصحة المعلومات المقدمة.</li>
          </Ul>
          <Para>ولا يتحمل الموقع أو المكتب أي مسؤولية عن رفع المستخدم لمستندات سرية أو غير مملوكة له أو لا يملك صلاحية مشاركتها.</Para>

          <H2 id="user-data">ثامناً: مسؤولية البيانات التي يشاركها المستخدم</H2>
          <Para>جميع البيانات والمستندات والمعلومات التي يقوم المستخدم بإدخالها أو رفعها أو إرسالها عبر الموقع أو خدمات الذكاء الاصطناعي يتم تقديمها ومشاركتها على مسؤولية طالب الخدمة وحده.</Para>
          <Para>ويقر المستخدم صراحةً بما يلي:</Para>
          <Ul>
            <li>أن جميع البيانات التي يقدمها صحيحة ودقيقة حسب علمه.</li>
            <li>أنه يتحمل المسؤولية عن أي بيانات ناقصة أو غير صحيحة أو مضللة.</li>
            <li>أنه يملك الحق القانوني في مشاركة البيانات والمستندات.</li>
            <li>أنه لا يجوز له مشاركة بيانات أو مستندات تخص الغير دون صفة أو موافقة قانونية.</li>
            <li>أنه يتحمل أي تبعات قانونية ناتجة عن تقديم بيانات أو مستندات لا يملك حق مشاركتها.</li>
            <li>أن الموقع والمكتب غير مسؤولين عن أي ضرر ينتج عن خطأ المستخدم أو نقص البيانات أو عدم صحة الوقائع المقدمة.</li>
          </Ul>

          <H2 id="privacy-collection">تاسعاً: الخصوصية وجمع البيانات</H2>
          <Para>قد يقوم الموقع بجمع ومعالجة البيانات اللازمة لتقديم الخدمة، ومنها:</Para>
          <Ul>
            <li>الاسم.</li>
            <li>رقم الهاتف.</li>
            <li>البريد الإلكتروني.</li>
            <li>بيانات الحساب وتسجيل الدخول.</li>
            <li>تفاصيل الطلب أو الاستشارة.</li>
            <li>المستندات والملفات المرفوعة.</li>
            <li>النصوص أو الوقائع التي يدخلها المستخدم في خدمات الذكاء الاصطناعي.</li>
            <li>بيانات الاستخدام والتصفح.</li>
            <li>معلومات تقنية مثل نوع الجهاز والمتصفح وعنوان الاتصال التقني بالقدر اللازم لتشغيل الموقع وتحسينه.</li>
          </Ul>
          <Para>ويتم استخدام هذه البيانات للأغراض التالية:</Para>
          <Ul>
            <li>تقديم الخدمات المطلوبة.</li>
            <li>التواصل مع المستخدم.</li>
            <li>تحليل الطلبات والمستندات.</li>
            <li>تحسين تجربة المستخدم.</li>
            <li>تطوير خدمات الموقع والذكاء الاصطناعي.</li>
            <li>حماية الموقع من إساءة الاستخدام.</li>
            <li>حفظ سجلات التعاملات.</li>
            <li>الامتثال لأي التزام قانوني أو تنظيمي أو قضائي.</li>
          </Ul>

          <H2 id="data-sharing">عاشراً: مشاركة البيانات مع الغير</H2>
          <Para>لا يتم بيع بيانات المستخدمين.</Para>
          <Para>وقد تتم مشاركة البيانات بالقدر اللازم في الحالات التالية:</Para>
          <Ul>
            <li>مع فريق العمل القانوني أو الإداري المختص داخل المكتب.</li>
            <li>مع مزودي الخدمات التقنية أو الاستضافة أو أنظمة الذكاء الاصطناعي أو أدوات التشغيل، متى كان ذلك لازماً لتقديم الخدمة.</li>
            <li>مع الجهات الرسمية أو القضائية أو الرقابية عند وجود التزام قانوني.</li>
            <li>مع الغير بموافقة المستخدم.</li>
            <li>عند الضرورة لحماية حقوق الموقع أو المكتب أو المستخدمين أو منع الاحتيال أو إساءة الاستخدام.</li>
          </Ul>
          <Para>ويقر المستخدم بأن بعض خدمات الذكاء الاصطناعي قد تتطلب معالجة البيانات تقنياً من خلال أنظمة أو مزودي خدمات، وأن إدخال البيانات في هذه الخدمات يتم بموافقته وعلى مسؤوليته.</Para>

          <H2 id="sensitive">الحادي عشر: البيانات الحساسة والسرية</H2>
          <Para>ينبغي على المستخدم عدم إدخال أو رفع أي بيانات شديدة الحساسية إلا إذا كانت ضرورية للخدمة المطلوبة.</Para>
          <Para>وتشمل البيانات الحساسة، على سبيل المثال، البيانات المالية والطبية والأسرية والجزائية، وبيانات الأطفال، الأسرار التجارية، المستندات القضائية، أو أي معلومات قد يترتب على إفشائها ضرر للغير.</Para>
          <Para>وفي جميع الأحوال، يقر المستخدم بأن مشاركة هذه البيانات تتم بإرادته وعلى مسؤوليته، وأنه يملك الصفة القانونية أو الموافقة اللازمة لمشاركتها.</Para>

          <H2 id="confidentiality">الثاني عشر: سرية المعلومات القانونية</H2>
          <Para>يبذل المكتب عناية معقولة في حماية سرية المعلومات التي تصل إليه من خلال الموقع.</Para>
          <Para>ومع ذلك، يقر المستخدم بأن استخدام الموقع أو إدخال البيانات في أنظمة إلكترونية أو خدمات ذكاء اصطناعي لا يوفر ضماناً مطلقاً ضد جميع المخاطر التقنية أو الاختراقات أو الأعطال أو أخطاء المستخدم.</Para>
          <Para>ولا تنشأ علاقة محاماة أو سرية مهنية كاملة لمجرد استخدام خدمة آلية أو إرسال استفسار عام، إلا بعد قبول المكتب تقديم خدمة قانونية محددة للمستخدم وفق اتفاق صريح.</Para>

          <H2 id="no-attorney">الثالث عشر: عدم إنشاء علاقة محاماة تلقائية</H2>
          <Para>لا يؤدي استخدام الموقع أو إنشاء حساب أو إرسال استفسار أو رفع مستند أو استخدام الذكاء الاصطناعي إلى إنشاء علاقة محاماة أو وكالة قانونية أو التزام بتمثيل المستخدم.</Para>
          <Para>ولا تنشأ العلاقة القانونية بين المكتب والمستخدم إلا بعد:</Para>
          <Ul>
            <li>قبول المكتب للطلب صراحة.</li>
            <li>الاتفاق على نطاق الخدمة.</li>
            <li>استكمال أي متطلبات مهنية أو قانونية.</li>
            <li>سداد الرسوم إن وجدت.</li>
            <li>عدم وجود تعارض مصالح أو مانع قانوني أو مهني.</li>
          </Ul>

          <H2 id="no-guarantee">الرابع عشر: عدم ضمان النتائج</H2>
          <Para>لا يضمن الموقع أو المكتب أو خدمات الذكاء الاصطناعي أي نتيجة قانونية أو قضائية أو إدارية أو تجارية.</Para>
          <Para>وتخضع نتائج أي قضية أو استشارة أو إجراء قانوني إلى الوقائع والمستندات والقانون المطبق وتقدير الجهات المختصة والمحاكم والسلطات ذات العلاقة.</Para>
          <Para>وأي تحليل أو رأي يصدر بناءً على بيانات ناقصة أو غير صحيحة يكون محدوداً بحدود المعلومات المقدمة من المستخدم.</Para>

          <H2 id="user-obligations">الخامس عشر: التزامات المستخدم</H2>
          <Ul>
            <li>استخدام الموقع لأغراض مشروعة فقط.</li>
            <li>عدم إساءة استخدام خدمات الذكاء الاصطناعي.</li>
            <li>عدم إدخال بيانات كاذبة أو مضللة.</li>
            <li>عدم رفع ملفات ضارة أو فيروسات أو روابط غير آمنة.</li>
            <li>عدم محاولة اختراق الموقع أو تعطيله أو التحايل على أنظمته.</li>
            <li>عدم استخدام الموقع للإساءة أو التشهير أو الابتزاز أو مخالفة القانون.</li>
            <li>عدم نسخ أو بيع أو إعادة استخدام محتوى الموقع دون إذن.</li>
            <li>احترام حقوق الملكية الفكرية وحقوق الغير.</li>
            <li>عدم استخدام الموقع لتقديم استشارات قانونية للغير أو انتحال صفة المكتب.</li>
          </Ul>

          <H2 id="content">السادس عشر: المحتوى القانوني المنشور في الموقع</H2>
          <Para>أي مقالات أو معلومات أو صفحات تعريفية أو محتوى قانوني منشور في الموقع يكون لأغراض التوعية العامة فقط.</Para>
          <Para>ولا يجوز اعتباره استشارة قانونية نهائية أو بديلاً عن مراجعة محامٍ مختص، لأن تطبيق القانون يختلف بحسب الوقائع والمستندات والظروف الخاصة بكل حالة.</Para>

          <H2 id="contracts">السابع عشر: العقود والنماذج القانونية</H2>
          <Para>في حال توفير الموقع لخدمة إنشاء العقود أو النماذج القانونية أو AI Contracts، فإن المستخدم يقر بأن هذه النماذج تكون مسودات أولية أو نماذج عامة قابلة للتعديل.</Para>
          <Para>ولا يضمن الموقع ملاءمة النموذج لكل حالة أو صفقة أو نزاع، ولا يجوز الاعتماد عليه نهائياً قبل مراجعته من محامٍ مختص.</Para>
          <Para>ويتحمل المستخدم مسؤولية البيانات التي يدخلها في العقد أو النموذج، ومسؤولية استخدامه أو توقيعه أو إرساله للغير.</Para>

          <H2 id="fees">الثامن عشر: الرسوم والمدفوعات</H2>
          <Para>قد تكون بعض الخدمات مجانية وبعضها مدفوعاً.</Para>
          <Para>وفي حال وجود رسوم، يلتزم المستخدم بسدادها وفق الآلية المعتمدة في الموقع أو المتفق عليها مع المكتب.</Para>
          <Para>ولا يحق للمستخدم استرداد الرسوم بعد بدء تنفيذ الخدمة أو تخصيص وقت مهني أو مراجعة المستندات أو تقديم التحليل أو الاستشارة، ما لم يقرر المكتب خلاف ذلك أو ينص اتفاق خاص على غير ذلك.</Para>

          <H2 id="refusal">التاسع عشر: رفض أو تعليق الخدمة</H2>
          <Para>يحق للموقع أو المكتب رفض أو تعليق أو إلغاء أي خدمة أو حساب في الحالات التالية:</Para>
          <Ul>
            <li>وجود تعارض مصالح.</li>
            <li>عدم اكتمال البيانات.</li>
            <li>تقديم بيانات غير صحيحة أو مضللة.</li>
            <li>استخدام الموقع بطريقة مخالفة للقانون.</li>
            <li>عدم سداد الرسوم.</li>
            <li>إساءة استخدام خدمات الذكاء الاصطناعي.</li>
            <li>رفع مستندات لا يملك المستخدم حق مشاركتها.</li>
            <li>وجود مانع مهني أو قانوني يحول دون تقديم الخدمة.</li>
            <li>مخالفة هذه الشروط والأحكام.</li>
          </Ul>

          <H2 id="liability">العشرون: حدود المسؤولية</H2>
          <Para>إلى أقصى حد يسمح به القانون، لا يتحمل الموقع أو المكتب أي مسؤولية عن:</Para>
          <Ul>
            <li>اعتماد المستخدم على مخرجات الذكاء الاصطناعي دون مراجعة قانونية.</li>
            <li>أي خطأ ناتج عن بيانات ناقصة أو غير صحيحة قدمها المستخدم.</li>
            <li>أي خسارة ناتجة عن استخدام الموقع أو تعذر استخدامه.</li>
            <li>أي أعطال تقنية أو توقف مؤقت للخدمة.</li>
            <li>أي اختراق أو وصول غير مصرح به خارج عن السيطرة المعقولة.</li>
            <li>أي ضرر ناتج عن مشاركة المستخدم لبيانات أو مستندات لا يملك حق مشاركتها.</li>
            <li>أي تصرف يتخذه المستخدم بناءً على معلومات عامة أو تحليل أولي.</li>
          </Ul>

          <H2 id="security">الحادي والعشرون: أمن المعلومات</H2>
          <Para>يتخذ الموقع تدابير فنية وتنظيمية معقولة لحماية البيانات، إلا أن نقل البيانات عبر الإنترنت لا يخلو من المخاطر.</Para>
          <Para>وعلى المستخدم اتخاذ الاحتياطات اللازمة، ومنها:</Para>
          <Ul>
            <li>عدم استخدام شبكات عامة غير آمنة عند رفع مستندات حساسة.</li>
            <li>عدم مشاركة كلمة المرور.</li>
            <li>التأكد من صحة الموقع قبل إدخال البيانات.</li>
            <li>عدم رفع مستندات غير ضرورية.</li>
            <li>إخفاء أو حذف البيانات الحساسة غير اللازمة متى أمكن.</li>
          </Ul>

          <H2 id="cookies">الثاني والعشرون: ملفات تعريف الارتباط والتحليلات</H2>
          <Para>قد يستخدم الموقع ملفات تعريف الارتباط أو أدوات التحليل لتحسين الأداء وتجربة المستخدم وقياس الزيارات وفهم طريقة استخدام الخدمات.</Para>
          <Para>ويمكن للمستخدم تعطيل ملفات تعريف الارتباط من إعدادات المتصفح، مع العلم أن بعض وظائف الموقع قد تتأثر.</Para>

          <H2 id="retention">الثالث والعشرون: حفظ البيانات وحذفها</H2>
          <Para>يجوز للمستخدم طلب تعديل أو حذف بياناته، وذلك وفق الإجراءات التي يعتمدها الموقع، وبما لا يتعارض مع الالتزامات القانونية أو المهنية أو المحاسبية أو متطلبات حفظ السجلات.</Para>
          <Para>ويجوز للموقع الاحتفاظ ببعض البيانات لمدة معقولة متى كان ذلك لازماً لإثبات التعاملات أو حماية الحقوق أو الامتثال للقانون أو إدارة النزاعات.</Para>

          <H2 id="ip">الرابع والعشرون: حقوق الملكية الفكرية</H2>
          <Para>جميع حقوق الملكية الفكرية المتعلقة بالموقع، شعاراته، تصميمه، نصوصه، نماذجه، قواعد بياناته، أدواته، خدماته، محتواه، وأي أنظمة ذكاء اصطناعي أو واجهات أو مواد منشورة فيه، مملوكة أو مرخصة للموقع أو المكتب.</Para>
          <Para>ولا يجوز نسخ أو نشر أو بيع أو تعديل أو إعادة استخدام أي جزء من الموقع أو محتواه دون موافقة كتابية مسبقة.</Para>

          <H2 id="comms">الخامس والعشرون: الاتصالات الإلكترونية</H2>
          <Para>يوافق المستخدم على تلقي الرسائل والإشعارات المتعلقة بحسابه أو طلباته أو خدماته عبر البريد الإلكتروني أو الهاتف أو الرسائل النصية أو تطبيقات التواصل أو من خلال الموقع.</Para>
          <Para>وتعد الرسائل المرسلة إلى بيانات التواصل التي قدمها المستخدم صحيحة ومنتجة لآثارها، ما لم يقم بتحديث بياناته.</Para>

          <H2 id="jurisdiction">السادس والعشرون: القانون الواجب التطبيق والاختصاص</H2>
          <Para>تخضع هذه الشروط والأحكام وسياسة الخصوصية لقوانين دولة الكويت.</Para>
          <Para>وتختص المحاكم الكويتية بالفصل في أي نزاع ينشأ عن استخدام الموقع أو الخدمات أو الحساب أو البيانات أو خدمات الذكاء الاصطناعي، ما لم يتم الاتفاق كتابةً على خلاف ذلك.</Para>

          <H2 id="acknowledgement">السابع والعشرون: الإقرار النهائي</H2>
          <Para>باستخدام الموقع أو إنشاء حساب أو رفع مستند أو إدخال بيانات أو استخدام خدمات الذكاء الاصطناعي، يقر المستخدم بما يلي:</Para>
          <Ul>
            <li>أنه قرأ الشروط والأحكام وسياسة الخصوصية ووافق عليها.</li>
            <li>أن جميع البيانات والمستندات التي يقدمها صحيحة ومقدمة على مسؤوليته.</li>
            <li>أنه يملك الحق القانوني في مشاركة أي بيانات أو مستندات يرفعها.</li>
            <li>أن خدمات الذكاء الاصطناعي خدمات مساعدة وأولية وليست رأياً قانونياً نهائياً.</li>
            <li>أن العلاقة القانونية مع المكتب لا تنشأ إلا بعد قبول صريح للخدمة.</li>
            <li>أن الموقع لا يضمن أي نتيجة قانونية أو قضائية أو إدارية.</li>
            <li>أنه يتحمل مسؤولية أي تصرف يتخذه بناءً على معلومات أو مخرجات غير مراجعة من محامٍ مختص.</li>
          </Ul>

          <div className="mt-16 pt-8 border-t border-slate-200 text-xs text-slate-500">
            آخر تحديث: فبراير 2026 — موقع ALDAKHEELLG.COM التابع لـ Aldakheel Legal Group.
          </div>
        </article>
      </div>
    </div>
  );
}
